import { Injectable } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';

import { IInputOption } from '@msslib/components/formly/models/input';
import { FormsValidators } from '@msslib/components/forms/validators/forms.validators';
import { AuthorizeService, FormatService, LtvService } from '@msslib/services';
import {
  IApplicants,
  IPropertyAndLoan,
  IUiAffordabilityModel,
  errorMessages,
  initialProductLengthTooltip,
} from 'apps/clubhub/src/app/ignite/models/affordability';
import { IgniteHelperService } from '@msslib/services/ignite.helper';
import { IgniteService } from 'apps/clubhub/src/app/ignite/services/ignite.service';
import {
  ApplicantCountry,
  BenefitIncomeType,
  BtlType,
  HolidayLetFrequency,
  MortgageLenders,
  MortgageType,
  ProductLength,
  ProductType,
  PropertyType,
  RemortgageReason,
  RemortgageType,
  RepaymentMethod,
  SkipListKeys,
  TaxStatusBtl,
  UkLocation,
} from 'apps/shared/src/models';
import { FormFieldType, WrapperType, yesNoOptions } from '@msslib/components/formly/formly.config';
import { LenderService } from 'apps/shared/src/services/lender.service';
import { map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AffordabilityFormFieldsBtlService {
  public constructor(
    private helperService: IgniteHelperService,
    private igniteService: IgniteService,
    private authService: AuthorizeService,
    private formatService: FormatService,
    private lenderService: LenderService,
    private ltvService: LtvService,
  ) { }

  public get model(): IUiAffordabilityModel | undefined {
    return this.igniteService.model;
  }

  public get allowCriteriaPlus() {
    return this.authService.allowCriteriaPlus;
  }

  public fields: FormlyFieldConfig[] = [
    // Step 1
    {
      key: 'propertyAndLoan',
      wrappers: [WrapperType.FormCard],
      props: {
        title: 'Property & Loan',
        summary: `<p>
          <i class="fas fa-info-circle"></i>
          All fields within this section are mandatory
        </p>`,
      },
      validators: {
        max500PercentageLoanValue: {
          expression: (_control: AbstractControl, field: FormlyFieldConfig) =>
            this.ltvService.isLtvValid(field.form?.getRawValue().propertyAndLoan as IPropertyAndLoan),
          message: errorMessages.ltv500Percent,
          errorPath: 'loanAmount',
        },
        lessThanDeposit: {
          expression: (control: AbstractControl, field: FormlyFieldConfig) => {
            const { loanAmount, propertyValue } = field.form?.getRawValue().propertyAndLoan as IPropertyAndLoan;
            const equityLoanValue = control.value.equityLoanValue;
            if (!equityLoanValue) {
              return true;
            }

            return equityLoanValue < propertyValue - loanAmount;
          },
          message: 'Equity loan value must be less than deposit',
          errorPath: 'equityLoanValue',
        },
        interestOnlyAmount: {
          expression: (control: AbstractControl, field: FormlyFieldConfig) => {
            const { loanAmount, interestOnlyAmount } = field.form?.getRawValue().propertyAndLoan as IPropertyAndLoan;
            if (!interestOnlyAmount) {
              return true;
            }

            return +interestOnlyAmount < +loanAmount;
          },
          message: errorMessages.interestOnlyLessThanLoanAmount,
          errorPath: 'interestOnlyAmount',
        },
        productTransferInitialDate: this.helperService.getProductTransferInitialPeriodValidation(),
      },
      fieldGroup: [
        {
          key: 'limitedCompanyPurchase',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'Limited company purchase',
            options: yesNoOptions,
            required: true,
          },
        },
        {
          key: 'limitedCompanySpecialPurposeVehicle',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'Is the Limited company in a Special Purpose Vehicle?',
            options: yesNoOptions,
            required: true,
          },
          expressions: {
            hide: field => !field.model?.limitedCompanyPurchase,
          },
        },
        {
          key: 'portfolioLandlord',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'Portfolio Landlord',
            tooltip: '4 or more Buy to Let or Consent to Let mortgaged properties (including this application)',
            options: yesNoOptions,
            required: true,
          },
          expressions: {
            hide: field => field.model?.limitedCompanyPurchase,
          },
        },
        {
          key: 'numberOfProperties',
          type: FormFieldType.Number,
          defaultValue: 0,
          props: {
            label: 'How many properties in the portfolio?',
            min: 4,
            required: true,
          },
          expressions: {
            hide: field => !field.model?.portfolioLandlord,
          },
        },
        {
          key: 'location',
          type: FormFieldType.Select,
          props: {
            label: 'Property location',
            change: (field: FormlyFieldConfig) => {
              const { location } = field.model;
              if (location === UkLocation.NorthernIreland) {
                field.form?.patchValue([{ helpToBuy: false }]);
              }
              this.igniteService.skipListChange.next(
                [SkipListKeys.PropertyLocation, SkipListKeys.HelpToBuyScheme],
              );
            },
            required: true,
            options: [
              this.helperService.defaultSelectOption,
              { value: UkLocation.EastMidlands, label: 'East Midlands' },
              { value: UkLocation.EastOfEngland, label: 'East of England' },
              { value: UkLocation.GreaterLondon, label: 'Greater London' },
              { value: UkLocation.NorthEast, label: 'North East' },
              { value: UkLocation.NorthWest, label: 'North West' },
              { value: UkLocation.SouthEast, label: 'South East' },
              { value: UkLocation.SouthWest, label: 'South West' },
              { value: UkLocation.WestMidlands, label: 'West Midlands' },
              { value: UkLocation.YorkshireAndHumberside, label: 'Yorkshire & Humberside' },
              { value: UkLocation.NorthernIreland, label: 'Northern Ireland' },
              { value: UkLocation.Scotland, label: 'Scotland' },
              { value: UkLocation.Wales, label: 'Wales' },
            ],
          },
        },
        {
          key: 'propertyType',
          type: FormFieldType.RadioButtons,
          defaultValue: PropertyType.House,
          props: {
            label: 'Property type',
            options: [
              { label: 'House', value: PropertyType.House },
              { label: 'Flat', value: PropertyType.Flat },
            ],
            required: true,
          },
        },
        {
          key: 'btlType',
          type: FormFieldType.Select,
          defaultValue: BtlType.Standard,
          props: {
            label: 'BTL type',
            options: this.helperService.productBtlTypeOptions,
            required: true,
          },
        },
        {
          key: 'numberOfBedrooms',
          type: FormFieldType.Number,
          defaultValue: 0,
          props: {
            label: 'Number of bedrooms',
            min: 2,
            max: 20,
            required: true,
          },
          expressions: {
            hide: field => field.model?.btlType !== BtlType.HMO,
          },
        },
        {
          key: 'numberOfBlocks',
          type: FormFieldType.Number,
          defaultValue: 0,
          props: {
            label: 'Number of units',
            min: 2,
            required: true,
          },
          expressions: {
            hide: field => field.parent?.model?.btlType !== BtlType.MUFB,
          },
        },
        {
          key: 'propertySelfContainedFlats',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'Does the property have self contained flats?',
            options: yesNoOptions,
            required: true,
          },
          expressions: {
            hide: field => field.model?.btlType !== BtlType.MUFB,
          },
        },
        {
          key: 'newBuild',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'New build',
            options: yesNoOptions,
            required: true,
          },
        },
        {
          key: 'mortgageType',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'Mortgage type',
            options: [
              { label: 'Purchase', value: MortgageType.Purchase },
              { label: 'Remortgage', value: MortgageType.Remortgage },
            ],
            required: true,
          },
        },
        {
          key: 'firstTimeBuyer',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'First time buyer',
            options: yesNoOptions,
            required: true,
          },
          expressions: {
            hide: field => field.model?.mortgageType !== MortgageType.Purchase,
          },
        },
        {
          key: 'firstTimeLandlord',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'First time landlord',
            options: yesNoOptions,
            required: true,
          },
          expressions: {
            hide: field => field.model?.mortgageType !== MortgageType.Purchase,
          },
        },
        {
          key: 'ownerOccupier',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'Owner occupier',
            options: yesNoOptions,
            required: true,
          },
          expressions: {
            hide: field =>
              field.model?.limitedCompanyPurchase
              || field.model?.firstTimeBuyer
              || field.model?.firstTimeBuyer === null
              || field.model?.mortgageType !== MortgageType.Purchase,
          },
        },
        {
          key: 'remortgageType',
          type: FormFieldType.Select,
          props: {
            label: 'Remortgage type',
            options: [
              { label: 'Let to buy', value: RemortgageType.LetToBuy },
              { label: 'Like for like', value: RemortgageType.LikeForLike },
              { label: 'Capital raising', value: RemortgageType.CapitalRaising },
            ],
            required: true,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.mortgageType !== MortgageType.Remortgage,
          },
        },
        ...this.mortgageLenderFields(),
        {
          key: 'amountTransferredFromOtherLender',
          type: FormFieldType.Currency,
          props: {
            label: 'Current mortgage balance outstanding',
            tooltip: 'What is the outstanding mortgage balance from the previous lender?',
            required: true,
          },
          expressions: {
            hide: field =>
              field.model?.mortgageType !== MortgageType.Remortgage
              || field.model?.remortgageType !== RemortgageType.CapitalRaising,
          },
        },
        {
          key: 'propertyValue',
          type: FormFieldType.Currency,
          props: {
            label: 'Property value',
            required: true,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
          expressions: {
            'props.readonly': () => this.igniteService.hasPresetPropertyValue,
          },
        },
        {
          key: 'loanAmount',
          type: FormFieldType.Currency,
          props: {
            label: 'Loan amount',
            required: true,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
          expressions: {
            'props.readonly': () => this.igniteService.hasPresetPropertyValue,
          },
        },
        {
          key: 'ltv',
          type: FormFieldType.Disabled,
          props: {
            label: 'LTV',
            addonEnd: '%',
          },
          expressions: {
            'props.value': (field) => this.ltvService.calculateLtv(field.model as IPropertyAndLoan),
          },
        },
        {
          key: 'equityLoanValue',
          type: FormFieldType.Currency,
          props: {
            label: 'Equity loan value',
            tooltip:`<p>
              Enter the amount of the equity loan from the government scheme.
              DO NOT include any of the customer's own deposit.
            </p>`,
          },
          expressions: {
            hide: field => field.model?.location === UkLocation.NorthernIreland
              || !field.model?.helpToBuy,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
        },
        {
          key: 'equityLoanValue',
          type: FormFieldType.Currency,
          props: {
            label: 'Equity loan value',
            tooltip: 'Enter the amount of the equity loan from the government scheme. DO NOT include any of the ' +
              'customer\'s own deposit.',
            required: true,
          },
          expressions: {
            hide: field => field.model?.location === UkLocation.NorthernIreland || !field.model?.helpToBuy,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
        },
        {
          key: 'remortgageReason',
          type: FormFieldType.Select,
          props: {
            label: 'Reason for remortgage',
            tooltip: 'What is the intention for the Help to Buy Equity loan Remortgage?',
            options: [
              { label: 'Purchase additional shares', value: RemortgageReason.PurchaseAdditionalShares },
              { label: '£ for £ remortgage', value: RemortgageReason.PoundForPound },
              { label: 'Other', value: RemortgageReason.Other },
            ],
            required: true,
          },
          expressions: {
            hide: (field) =>
              (field.model as IPropertyAndLoan).mortgageType !== MortgageType.Remortgage ||
              !(field.model as IPropertyAndLoan).helpToBuy,
            'props.disabled': (field) => (field.model as IPropertyAndLoan)
              .productTypeExtended === ProductType.RetirementInterestOnly,
          },
        },
        ...this.repaymentMethodFields(true),
        {
          key: 'holidayLetIncome',
          expressions: {
            hide: field => field.parent?.model?.btlType !== BtlType.HolidayLet,
          },
          props: {
            suppressErrors: true,
          },
          fieldGroup: [
            {
              type: FormFieldType.Separator,
              props: {
                header: 'Holiday Let Income',
                headerTooltip: 'Enter expected Low, Mid, and High seasonal rentals',
              },
            },
            {
              key: 'holidayLetIncomeLow',
              type: FormFieldType.Currency,
              props: {
                label: 'Low season rental amount received / expected',
              },
            },
            {
              key: 'holidayLetIncomeMedium',
              type: FormFieldType.Currency,
              props: {
                label: 'Mid season rental amount received / expected',
              },
            },
            {
              key: 'holidayLetIncomeHigh',
              type: FormFieldType.Currency,
              props: {
                label: 'High season rental amount received / expected',
              },
            },
            {
              key: 'holidayLetIncomeFrequency',
              type: FormFieldType.Select,
              defaultValue: null,
              props: {
                options: this.helperService.buildSelectOptionsFromEnum(HolidayLetFrequency),
                label: 'Payment frequency',
              },
            },
            {
              template: `
                <hr class="mt-4" />
              `,
            },
          ],
        },
        {
          key: 'rentalAmountReceivedExpected',
          type: FormFieldType.Currency,
          props: {
            label: 'Monthly rental amount received / expected',
            tooltip: 'What is the monthly rental amount received / expected if let on Assured Shorthold Tenancy basis',
            required: true,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
        },
        {
          key: 'interestOnlyAmount',
          type: FormFieldType.Currency,
          props: {
            label: 'Amount on interest only',
            required: true,
          },
          expressions: {
            hide: field => field.model?.repaymentMethod.value !== RepaymentMethod.InterestOnlyPartAndPart,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
        },
        {
          type: FormFieldType.Disabled,
          props: {
            label: 'Repayment amount',
            addonStart: '£',
            testId: 'repaymentAmount',
          },
          expressions: {
            hide: (field) =>
              (field.model as IPropertyAndLoan).repaymentMethod.value !== RepaymentMethod.InterestOnlyPartAndPart,
            'props.value': (field) => {
              const { loanAmount, interestOnlyAmount } = field.model as IPropertyAndLoan;
              const repaymentAmount = this.helperService.calculateRepaymentAmount(loanAmount, interestOnlyAmount);
              return repaymentAmount ? this.formatService.formatCurrency(repaymentAmount) : 0;
            },
          },
        },
        {
          key: 'mortgageTerm',
          props: {
            label: 'Mortgage term',
            required: true,
            groupedField: true,
          },
          validators: {
            ...this.helperService.yearMonthValidator,
            max40Years: {
              expression: (control: AbstractControl) => {
                const { years, months } = control.value;
                return this.helperService.isMortgageTermValid(years, months);
              },
              message: this.max40YearsValidationMessage,
            },
          },
          fieldGroupClassName: 'row',
          fieldGroup: this.helperService.yearsAndMonthsFields(),
        },
        {
          key: 'productLength',
          type: FormFieldType.Select,
          defaultValue: ProductLength.TwoYears,
          props: {
            label: 'Initial product period',
            tooltip: initialProductLengthTooltip,
            options: this.helperService.productLengthBtlOptions,
            required: true,
          },
        },
      ],
    },
    // Step 2
    {
      key: 'applicants',
      wrappers: [WrapperType.FormCard],
      props: {
        title: 'Applicants',
        summary: `<p>
          <i class="fas fa-info-circle"></i>
          All fields within this section are mandatory
        </p>`,
      },
      validators: {
        totalDependants: {
          expression: (control: AbstractControl) => {
            const {
              financialDependants,
              dependantsAged0To5,
              dependantsAged6To11,
              dependantsAged12To17,
              dependantsAged18AndOver,
            } = control.value as IApplicants;
            if (!financialDependants) {
              return true;
            }
            const total = +dependantsAged0To5 + +dependantsAged6To11 + +dependantsAged12To17 + +dependantsAged18AndOver;
            return total > 0 && total <= 20;
          },
          message: 'Total dependants must be between 1 and 20',
          errorPath: 'dependantsAged18AndOver',
        },
      },
      fieldGroup: [
        {
          key: 'numberOfApplicants',
          type: FormFieldType.RadioButtons,
          defaultValue: 1,
          props: {
            label: 'Number of applicants',
            options: [
              { label: '1', value: 1 },
              { label: '2', value: 2 },
              { label: '3', value: 3 },
              { label: '4', value: 4 },
            ],
            required: true,
            change: (field: FormlyFieldConfig) => {
              const numberOfApplicants = field.model.numberOfApplicants;
              this.onNumberOfApplicantsChange(numberOfApplicants, numberOfApplicants);

              this.fields = [...this.fields];
            },
          },
        },
        {
          key: 'applicantAges',
          type: FormFieldType.Repeating,
          props:{
            suppressErrors: true,
          },
          wrappers: [WrapperType.NoWrappers],
          fieldArray: {
            wrappers: [WrapperType.NoWrappers],
            props:{
              suppressErrors: true,
            },
            fieldGroup: [
              {
                expressions: {
                  template: field => {
                    const summary = +field.model.applicantNumber === 1 ? `
                      <p class="mb-3">
                        Enter the applicants in the order of how you want their income to be considered
                      </p>
                    ` : '';
                    return `
                      <hr class="my-0" />
                      <h2 class="fw-light mt-4 mb-3">Applicant ${field.model.applicantNumber}</h2>
                      ${summary}
                    `;
                  },
                },
              },
              {
                key: 'dateOfBirth',
                type: FormFieldType.Date,
                props: {
                  label: 'Date of birth',
                  required: true,
                },
                validators: {
                  validation: [FormsValidators.date, FormsValidators.atLeast18Years],
                },
              },
              {
                key: 'countryResident',
                type: FormFieldType.Select,
                props: {
                  label: 'What country is the applicant currently resident in',
                  options: this.helperService.countryResidentTypeOptions,
                  required: true,
                },
              },
              {
                key: 'grossAnnualIncome',
                type: FormFieldType.Currency,
                props: {
                  label: 'Gross Annual Income',
                  required: true,
                  tooltip: 'Please include total of all income before tax.',
                },
                validators: {
                  validation: [FormsValidators.greaterThan(0)],
                },
              },
              {
                key: 'taxStatusBtl',
                type: FormFieldType.Select,
                props: {
                  label: 'Tax status',
                  required: true,
                },
                validators: {
                  validation: [FormsValidators.isEnumMember(TaxStatusBtl)],
                },
                expressions: {
                  'props.options': (field) => {
                    const countryResident  = field.model?.countryResident;
                    if (countryResident === ApplicantCountry.Scotland) {
                      return this.helperService.buildSelectOptionsFromEnum(TaxStatusBtl)
                        .filter((option) => option.value !== TaxStatusBtl.Additional);
                    }
                    return this.helperService
                      .buildSelectOptionsFromEnum(TaxStatusBtl)
                      .filter(
                        (option) =>
                          option.value !== TaxStatusBtl.Top &&
                          option.value !== TaxStatusBtl.Starter &&
                          option.value !== TaxStatusBtl.Intermediate,
                      );
                  },
                  hide: field => field.model?.countryResident === null,
                },
              },
            ],
          },
        },
        // Submit button to always be the in last card. Hide *this* button if top slicing is 'Yes' (i.e. bigger form)
        {
          key: 'proceedBtn',
          type: 'buttons',
          props: {
            buttons: [
              { label: 'Proceed', type: 'submit', className: 'btn-2022--secondary' },
            ],
          },
        },
      ],
    },
  ];

  public get benefitIncomeCustomOptions(): IInputOption[] {
    return [
      this.helperService.defaultSelectOption,
      { value: BenefitIncomeType.ChildBenefit, label: 'Child benefit' },
      { value: BenefitIncomeType.ChildTaxCredits, label: 'Child tax credits' },
      { value: BenefitIncomeType.WorkingTaxCredits, label: 'Working tax credits' },
      { value: BenefitIncomeType.CarersAllowance, label: 'Carers allowance' },
      { value: BenefitIncomeType.DlaOrPip, label: 'DLA or PIP' },
      { value: BenefitIncomeType.UniversalCredit, label: 'Universal credit' },
    ];
  }

  private max40YearsValidationMessage(error, field: FormlyFieldConfig): string {
    return `${field.parent?.props?.text} has a maximum of 40 years`;
  }

  public templateHeader(title: string): FormlyFieldConfig {
    return {
      template: `
        <hr class="separator">
        <h2>${title}</h2>
      `,
    };
  }

  private mortgageLenderFields(): FormlyFieldConfig[] {
    const mortgageLenderHidden = (field) => {
      return (field?.model?.mortgageType !== MortgageType.Remortgage
        && field?.model?.mortgageType !== MortgageType.Purchase)
        || field?.model?.firstTimeBuyer === true;
    };

    const initialPeriodHidden = (field) => field?.model?.mortgageType !== MortgageType.Remortgage;

    return [
      {
        key: 'mortgageLender',
        wrappers: [WrapperType.NoWrappers],
        props: {
          suppressErrors: true,
        },
        fieldGroup: [
          {
            key: 'value',
            type: FormFieldType.Select,
            props: {
              label: 'Who is the current mortgage Lender?',
              allowNull: true,
            },
            expressions: {
              hide: field => mortgageLenderHidden(field.parent?.parent),
              'props.options': this.lenderService.getUnarchivedLendersNames(this.helperService.getBtlLendingType())
                .pipe(map(lenderNames => this.helperService.getMortgageLendersList(lenderNames))),
            },
          },
        ],
      },
      {
        key: 'currentMortgageLender',
        type: FormFieldType.Input,
        expressions: {
          hide: field => {
            if (mortgageLenderHidden(field)) {
              return true;
            }

            const mortgageLenderValue = field.model?.mortgageLender?.value;
            return !mortgageLenderValue || mortgageLenderValue !== MortgageLenders.Other;
          },
        },
      },
      {
        key: 'productTransferInitialDate',
        type: FormFieldType.Date,
        props: {
          label: 'What is the end date for the Initial period?',
        },
        validators: {
          validation: [FormsValidators.dateOrEmpty],
        },
        expressions: {
          hide: field => {
            if (initialPeriodHidden(field)) {
              return true;
            }

            return field.model?.mortgageLender?.value === MortgageLenders.Other;
          },
        },
      },
    ];
  }

  private repaymentMethodFields(showAllFields: boolean): FormlyFieldConfig[] {
    return [
      {
        key: 'repaymentMethod',
        wrappers: [WrapperType.NoWrappers],
        expressions: {
          'props.text': (field) => {
            if (showAllFields) {
              return 'Method of repayment';
            }
            switch (field.model as RepaymentMethod) {
              case RepaymentMethod.InterestOnly:
                return 'Method of repayment - Interest Only';
              case RepaymentMethod.InterestOnlyPartAndPart:
                return 'Method of repayment - Interest Only Part & Part';
            }
          },
        },
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            key: 'value',
            type: FormFieldType.Select,
            defaultValue: RepaymentMethod.InterestOnly,
            hide: !showAllFields,
            props: {
              label: 'Method of repayment',
              options: this.helperService.buildSelectOptionsFromEnum(RepaymentMethod),
              required: true,
            },
            expressions: {
              'props.readonly': field => field.parent?.parent?.model.helpToBuy ||
                (field.parent?.parent?.formControl as UntypedFormGroup).controls?.productTypeExtended
                  ?.value === ProductType.RetirementInterestOnly,
            },
          },
        ],
      },
    ];
  }

  private onNumberOfApplicantsChange(numberOfApplicants: number, numberOfIncomesNeeded: number) {
    if (!this.model) {
      this.igniteService.updateModel();
    }
    if (this.model?.applicants.applicantAges) {
      if ((this.model.applicants.applicantAges?.length ?? 0) > numberOfApplicants) {
        this.model.applicants.applicantAges = this.model.applicants.applicantAges.slice(0, numberOfApplicants);
      }
      if (
        (this.model.incomeAndExpenditure?.applicants?.length ?? 0) > numberOfIncomesNeeded &&
        this.model.incomeAndExpenditure.applicants
      ) {
        this.model.incomeAndExpenditure.applicants =
          this.model.incomeAndExpenditure.applicants.slice(0, numberOfIncomesNeeded);
      }

      if (this.model.applicants.applicantAges) {
        while (this.model.applicants.applicantAges.length < numberOfApplicants) {
          this.igniteService.addApplicant();
        }
      }
      while ((this.model.incomeAndExpenditure?.applicants?.length ?? 0) < numberOfIncomesNeeded) {
        this.igniteService.addApplicantIncome();
      }
    }

    this.igniteService.updateModel();
  }
}
